import { Box } from "@material-ui/core";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import React, { forwardRef } from "react";

import { NORMAL_KEY } from "../../../hooks/charts/useClimateMonthlySeries";

/* Mixed chart: bars for the observed water years, a line for the baseline.
   react-chartjs-2 pulls in chart.js/auto, so these are already registered;
   listing them keeps the dependency explicit. */
ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
);

/* A month that is still running, or missing days, is drawn faded so it reads
   as provisional next to the complete months beside it. */
const INCOMPLETE_ALPHA = 0.35;

const fade = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const signed = (value, decimals) =>
  `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value).toFixed(
    decimals
  )}`;

/**
 * Grouped bars by water-year month, October through September.
 *
 * Adapted from components/charts/yearBarChart/YearBarChart.jsx: the same
 * chart.js grouped-bar setup, but the series are named keys rather than
 * calendar years, the axis runs on the water year, and incomplete months are
 * drawn faded.
 */
const ClimateMonthlyChart = forwardRef(function ClimateMonthlyChart(
  {
    series,
    seriesKeys,
    cellMeta = {},
    unit,
    axisLabel,
    decimals = 2,
    normalLabel,
    normalYears,
    height = 400,
    fixedExtent,
    ariaLabel,
  },
  ref
) {
  const chartData = {
    labels: series.map((row) => row.month),
    datasets: seriesKeys.map((entry) => {
      const values = series.map((row) => row[entry.key] ?? null);

      /* The long-term average is a reference level, not another observation,
         so it is drawn as a line over the bars rather than as a fourth bar.
         It also stays readable once comparison years are switched on, where
         it would otherwise be one sliver among nine. A lower `order` puts it
         on top of the bars. */
      if (entry.isNormal) {
        return {
          type: "line",
          label: entry.label,
          data: values,
          borderColor: entry.color,
          backgroundColor: entry.color,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 6,
          /* White halo round each point so the marker still reads where the
             line crosses a bar. */
          pointBorderColor: "#fff",
          pointBorderWidth: 1,
          pointBackgroundColor: entry.color,
          fill: false,
          tension: 0,
          order: 0,
        };
      }

      const backgroundColor = series.map((row) =>
        cellMeta[`${entry.key}|${row.wy_month_order}`]?.incomplete
          ? fade(entry.color, INCOMPLETE_ALPHA)
          : entry.color
      );
      return {
        type: "bar",
        label: entry.label,
        data: values,
        backgroundColor,
        borderColor: entry.color,
        borderWidth: series.map((row) =>
          cellMeta[`${entry.key}|${row.wy_month_order}`]?.incomplete ? 2 : 0
        ),
        maxBarThickness: 18,
        order: 1,
      };
    }),
  };

  const options = {
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      axis: "x",
      intersect: false,
    },
    plugins: {
      tooltip: {
        mode: "index",
        axis: "x",
        intersect: false,
        padding: 12,
        borderWidth: 1,
        borderColor: "#999",
        backgroundColor: "#fff",
        titleColor: "#333",
        bodyColor: "#333",
        cornerRadius: 6,
        titleFont: { weight: "bold", size: 14 },
        bodyFont: { size: 14 },
        callbacks: {
          title: (items) => items?.[0]?.label ?? "",
          label: (item) => {
            const value = item.raw;
            if (value == null) return undefined;
            return `${item.dataset.label}: ${value.toFixed(decimals)} ${unit}`;
          },
          afterLabel: (item) => {
            const value = item.raw;
            if (value == null) return undefined;
            const entry = seriesKeys[item.datasetIndex];
            if (!entry) return undefined;

            if (entry.key === NORMAL_KEY) {
              return normalYears
                ? `Average of ${normalYears} water years`
                : undefined;
            }

            const order = series[item.dataIndex]?.wy_month_order;
            const meta = cellMeta[`${entry.key}|${order}`];
            const lines = [];
            if (meta?.departure != null) {
              lines.push(
                `${signed(meta.departure, decimals)} ${unit} vs ${normalLabel}`
              );
            }
            if (Number.isFinite(meta?.daysWithData)) {
              lines.push(
                `${meta.daysWithData} of ${meta.daysInMonth} days of data` +
                  (meta.incomplete ? " (incomplete)" : "")
              );
            }
            return lines.length ? lines : undefined;
          },
        },
      },
      legend: {
        position: "top",
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Water year month (October to September)",
        },
      },
      y: {
        title: { display: true, text: axisLabel },
        beginAtZero: true,
        ...(fixedExtent ? { min: fixedExtent[0], max: fixedExtent[1] } : {}),
      },
    },
  };

  return (
    <Box height={height} width="100%">
      <Bar
        ref={ref}
        data={chartData}
        options={options}
        role="img"
        aria-label={ariaLabel}
        fallbackContent={
          <p>
            {ariaLabel} The same figures are listed in the table below this
            chart.
          </p>
        }
      />
    </Box>
  );
});

export default ClimateMonthlyChart;
